import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { useNotices } from '@/features/notices/useNotices';
import { useFormSave } from '@/shared/lib/hooks/use-form-save';
import { CollectionName, ContactDto, ContactType } from '@/shared/lib/sj-orm';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BankForm } from './BankForm';
import { BrokerForm } from './BrokerForm';
import { IndividualForm } from './IndividualForm';
import { LegalEntityForm } from './LegalEntity';

type FastContactFormProps = {
  type: ContactType;
  formId: string;
  onClose: () => void;
  onSave?: (dto: ContactDto) => void;
};

export const FastContactForm: FC<FastContactFormProps> = ({ type, formId, onClose, onSave }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'fastCreateContact' });
  const notices = useNotices();

  const { onSubmit: formOnSubmit, finish } = useFormSave({
    collectionName: CollectionName.CONTACTS,
    updateOrSave: 'save',
    initialDtoData: { type },
  });

  const form = useForm<ContactDto>({
    defaultValues: { type },
  });
  const { control, handleSubmit: formHandleSubmit } = form;

  const onSubmit = async (data: ContactDto) => {
    const dto = { ...data };

    formOnSubmit(dto);
    const result = await finish(dto);

    if (result?.id) {
      onClose();
      onSave?.(result as ContactDto);
    } else {
      notices.error(t('errors.createContact'));
    }
  };

  const onError = () => {
    notices.error(t('formValidationError'));
  };

  const formProps = {
    control,
    formId,
    onSubmit: formHandleSubmit(onSubmit, onError),
    form,
  };

  switch (type) {
    case ContactType.BANK:
      return <BankForm {...formProps} />;
    case ContactType.BROKER:
      return <BrokerForm {...formProps} />;
    case ContactType.LEGAL_ENTITY:
      return <LegalEntityForm {...formProps} />;
    case ContactType.INDIVIDUAL:
      return <IndividualForm {...formProps} />;
    default:
      return null;
  }
};
